import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from 'react-query';

interface Permissions {
  can_write_board: boolean;
  can_write_todo: boolean;
  can_write_notes: boolean;
}

interface PermissionsContextType {
  permissions: Permissions;
  isLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Always allow everyone to edit everything
const defaultPermissions: Permissions = {
  can_write_board: true,
  can_write_todo: true,
  can_write_notes: true
};

interface PermissionsProviderProps {
  children: ReactNode;
  roomId: string;
  userName: string;
}

export const PermissionsProvider = ({ children, roomId, userName }: PermissionsProviderProps) => {
  // Always return full permissions for everyone
  const { data: permissions = defaultPermissions, isLoading } = useQuery(
    ['userPermissions', roomId, userName],
    () => Promise.resolve(defaultPermissions),
    {
      staleTime: Infinity, // Never stale since we're hardcoding the values
    }
  );

  return (
    <PermissionsContext.Provider value={{ permissions, isLoading }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
